import classNames from "classnames";
import { FunctionComponent, PropsWithChildren, SVGProps } from "react";
import Button, { ButtonVariants } from "shared/UI/Buttons/Button";
import { useMatchMedia } from "shared/UI/Hooks/useMatchMedia";
import { Container, NarrowContainer } from "shared/UI/LayoutComponents/LayoutComponents";
import { ResponsiveBreakpoints } from "shared/UI/ResponsiveBreakpoints";
import { PageHeader } from "UIPalette/PageHeader/PageHeader";

export interface PageModalCta {
  title: string;
  onClick: () => void;
}

type PageModalProps = {
  variant?: "red";
  backTitle?: string;
  nextTitle?: string;
  backIcon?: FunctionComponent<SVGProps<SVGSVGElement>>;
  nextIcon?: FunctionComponent<SVGProps<SVGSVGElement>>;
  onClose?: () => void;
  onNext?: () => void;
  primaryCta?: PageModalCta;
  primaryCtaVariant?: ButtonVariants;
  secondaryCtaVariant?: ButtonVariants;
  secondaryCta?: PageModalCta;
  textCta?: PageModalCta;
  showMobileIcon?: boolean;
  hideBackForDesktop?: boolean;
} & PropsWithChildren;

export default function PageModal({
  showMobileIcon = true,
  hideBackForDesktop,
  variant,
  backTitle,
  nextTitle,
  backIcon,
  nextIcon,
  onClose,
  onNext,
  primaryCta,
  primaryCtaVariant,
  secondaryCta,
  secondaryCtaVariant,
  textCta,
  children,
}: PageModalProps) {
  const largerDisplay = useMatchMedia(ResponsiveBreakpoints.Desktop);
  const isMobile = !largerDisplay;
  const hasCta = !!primaryCta || !!secondaryCta || !!textCta;

  let gradient;
  switch (variant) {
    case "red":
      gradient = "bg-gradient-to-b from-brand-red-lighter";
      break;
    default:
      gradient = "";
  }

  const primaryButton = (className?: string) =>
    !!primaryCta && (
      <Button
        variant={primaryCtaVariant}
        className={className}
        text={primaryCta?.title}
        aria-label={primaryCta?.title}
        onClick={primaryCta?.onClick}
      />
    );

  const secondaryButton = (className?: string) =>
    !!secondaryCta && (
      <Button
        variant={secondaryCtaVariant}
        className={className}
        text={secondaryCta?.title}
        aria-label={secondaryCta?.title}
        onClick={secondaryCta?.onClick}
      />
    );

  return (
    <div
      data-testid="page-modal"
      className={classNames(
        "overflow-y-scroll fixed left-0 top-0 lg:pt-20 h-screen w-full z-MODAL lg:z-OVERLAY bg-white to-transparent p-4",
        gradient
      )}
    >
      <Container className={classNames("!mb-0", { "!h-auto": isMobile && hasCta })}>
        {!(isMobile && !showMobileIcon) && (
          <PageHeader
            hideBackForDesktop={hideBackForDesktop}
            backIcon={backIcon}
            backTitle={isMobile ? "" : backTitle}
            onClickBack={onClose}
            onClickNext={!isMobile ? onNext : undefined}
            nextIcon={nextIcon}
            nextTitle={nextTitle}
          />
        )}
        <div className={classNames("flex flex-col gap-10 justify-center")}>
          <NarrowContainer
            className={classNames({ "h-screen": isMobile && !hasCta, "flex items-center md:items-start justify-center -mt-10 md:mt-0": !hasCta })}
          >
            <div className={classNames({ "min-h-[calc(100vh-300px)]": isMobile && hasCta }, "flex flex-col justify-center")}>{children}</div>
            {isMobile && hasCta && <div className="h-[150px] w-full"></div>}
            <div data-testid="desktop-cta-buttons" className="hidden my-10 lg:block space-y-4">
              {secondaryButton()}
              {primaryButton()}
            </div>
          </NarrowContainer>
          {hasCta && (
            <div className="fixed left-0 px-8 bottom-10 w-full flex flex-col gap-4 justify-center items-center lg:hidden">
              <div className="w-full max-w-[500px] space-y-4">
                {secondaryButton()}
                {primaryButton()}
                <Button variant="text" text={textCta?.title} aria-label={textCta?.title} onClick={textCta?.onClick} />
              </div>
            </div>
          )}
        </div>
      </Container>
    </div>
  );
}
